"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AUTH_STORAGE_KEY = void 0;
const ClientOAuth2 = require("client-oauth2");
const queryString = require('query-string');
const globals_1 = require("./globals");
// Defaults:
const AUTH_TOKEN_INTERVAL = 30 * 1000; // every 30 seconds
const AUTH_REFRESH_WINDOW = 5; // minutes before token expiration that we renew
exports.AUTH_STORAGE_KEY = 'lo-app-tools-auth';
const LO_QUERY_STRING_PARAMS = ['account', 'projectId', 'cohortId'];
class LOAuth {
    constructor(options) {
        const required = [
            'clientId',
            'authorizationUri',
            'accessTokenUri',
            'redirectUri',
            'logoutUri',
            'logoutRedirectUri',
            'scopes'
        ];
        for (const param of required) {
            // eslint-disable-next-line security/detect-object-injection
            if (!options[param]) {
                throw new Error(`LOAuth ctor param ${param} is required`);
            }
        }
        const state = this._getStateForClientOAuth(options);
        this.client = new ClientOAuth2({
            clientId: options.clientId,
            authorizationUri: options.authorizationUri,
            accessTokenUri: options.accessTokenUri,
            redirectUri: options.redirectUri,
            scopes: options.scopes,
            state
        });
        this.clientOptions = options;
        this.clientOptions.storageKey = options.storageKey || exports.AUTH_STORAGE_KEY;
        this.clientOptions.storage = options.storage || globals_1.window.localStorage;
    }
    _getAppUri() {
        this._decodeAppState();
        const protocol = globals_1.window.location.protocol;
        const hostname = globals_1.window.location.hostname;
        const port = globals_1.window.location.port ? ':' + globals_1.window.location.port : '';
        const queryParameters = new URLSearchParams();
        LO_QUERY_STRING_PARAMS.forEach((param) => {
            if (this.appState[param]) {
                queryParameters.set(param, this.appState[param]);
            }
        });
        let queryString = queryParameters.toString();
        queryString = queryString.length ? `?${queryString}` : '';
        const pathname = this.appState.pathname || '';
        return `${protocol}//${hostname}${port}${pathname}${queryString}`;
    }
    _decodeAppState() {
        const queryParameters = new URLSearchParams(globals_1.window.location.search);
        try {
            this.appState = {};
            // If initial load, read from queryStrings
            LO_QUERY_STRING_PARAMS.forEach((param) => {
                if (queryParameters.get(param)) {
                    this.appState[param] = queryParameters.get(param);
                }
            });
            // After redirect pathname will be the root directory, but don't add that to
            // the appState object because it messes up the Object key ordering and causes
            // the state comparison to fail
            if (globals_1.window.location.pathname && globals_1.window.location.pathname !== '/') {
                this.appState.pathname = globals_1.window.location.pathname;
            }
            // If after login flow, decode from state
            const queryState = queryParameters.get('state');
            if (queryState) {
                Object.assign(this.appState, JSON.parse(atob(queryState)));
            }
        }
        catch (error) {
            console.warn(error, 'Error occurred parsing state query string parameter');
        }
    }
    _getStateForClientOAuth(options) {
        this._decodeAppState();
        const state = Object.assign(Object.assign({}, this.appState), options.appState);
        if (Object.keys(state).length === 0) {
            return undefined;
        }
        return btoa(JSON.stringify(state));
    }
    /**
     * Sets a domain cookie for the token data in a format compatible with this library
     * The cookie can be read by another application on a compatible domain by triggering
     * refreshAuthToken, which will find and import the cookie as a token
     */
    static setDomainCookieAuthState(token) {
        globals_1.document.cookie = `lo-app-tools-auth=${JSON.stringify({
            access_token: token.access_token,
            refresh_token: token.refresh_token,
            expires: token.expires,
            clientId: token.clientId,
            cookieDomain: token.cookieDomain
        })};domain=.${token.cookieDomain};Max-Age=10;path=/;secure`;
    }
    /**
     * Sets a domain cookie with the current authentication token
     */
    setDomainCookieAuthState(cookieDomain) {
        var _a;
        if (!this.token) {
            return;
        }
        LOAuth.setDomainCookieAuthState({
            access_token: this.token.accessToken,
            refresh_token: this.token.refreshToken,
            expires: (_a = this.token.expires) === null || _a === void 0 ? void 0 : _a.getTime(),
            clientId: this.clientOptions.clientId,
            cookieDomain
        });
    }
    /**
     * This method looks for a cookie set by setDomainCookieAuthState that allows the
     * applications to exchange auth tokens over different subdomains
     */
    _getDomainCookieAuthState() {
        try {
            if (!globals_1.document.cookie) {
                return;
            }
            const cookie = globals_1.document.cookie
                .split('; ')
                .find((cookie) => cookie.startsWith(`${this.clientOptions.storageKey || exports.AUTH_STORAGE_KEY}=`));
            const value = cookie && cookie.split('=')[1];
            if (!value) {
                return;
            }
            const storedData = JSON.parse(value);
            const token = this.client.createToken({
                access_token: storedData.access_token,
                refresh_token: storedData.refresh_token,
                token_type: 'Bearer'
            });
            token.expiresIn(storedData.expires - Date.now());
            // the cookie clientId takes precedent over configured options
            this.clientOptions.clientId = storedData.clientId;
            // delete the cookie after it's read
            globals_1.document.cookie = `${exports.AUTH_STORAGE_KEY}=;domain=.${storedData.cookieDomain};Max-Age=-9999;path=/;secure`;
            return token;
        }
        catch (err) {
            console.warn('Failed to parse domain cookie for authentication tokens', err);
            return;
        }
    }
    _storeTokenData(token) {
        const { storage, storageKey } = this.clientOptions;
        const data = Object.assign(Object.assign({}, token.data), { expires: token.expires.getTime() });
        storage.setItem(storageKey, JSON.stringify(data));
    }
    _getTokenDataFromStorage() {
        const { storage, storageKey } = this.clientOptions;
        const value = storage.getItem(storageKey);
        return value ? JSON.parse(value) : null;
    }
    _removeTokenDataFromStorage() {
        const { storage, storageKey } = this.clientOptions;
        storage.removeItem(storageKey);
    }
    _isTokenExpiring(options = {}) {
        const refreshWindow = options.refreshWindow !== undefined
            ? options.refreshWindow
            : AUTH_REFRESH_WINDOW;
        return (Math.floor((this.token.expires.getTime() - Date.now()) / 1000 / 60) <=
            refreshWindow);
    }
    refreshAccessToken(options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            options = Object.assign({}, this.clientOptions, options);
            try {
                // initially, attempt to get the token from a domain cookie set by phc-login
                if (!this.token) {
                    this.token = this._getDomainCookieAuthState();
                    if (this.token) {
                        this._storeTokenData(this.token);
                    }
                }
                if (!this.token) {
                    // Initial auth token exchange
                    this.token = yield this.client.code.getToken(globals_1.window.location.href, options);
                    this._storeTokenData(this.token);
                    // Remove client_id / code from URL
                    globals_1.window.history.replaceState({}, globals_1.window.document.title, this._getAppUri());
                }
                else if (options.expiringRefresh) {
                    // Token refresh
                    const response = yield globals_1.window.fetch(this.clientOptions.accessTokenUri, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        },
                        body: queryString.stringify({
                            client_id: options.clientId,
                            grant_type: 'refresh_token',
                            refresh_token: this.token.refreshToken,
                            redirect_uri: this.clientOptions.redirectUri
                        })
                    });
                    // Only create/store tokens from a valid response
                    if (response.ok) {
                        const responseJson = yield response.json();
                        this.token = this.client.createToken(Object.assign({
                            refresh_token: this.token.refreshToken
                        }, responseJson));
                        this._storeTokenData(this.token);
                    }
                    else {
                        // Go back to the no token path if refresh failed
                        // This will result in an auth redirect
                        this.token = null;
                        this._removeTokenDataFromStorage();
                        yield this.refreshAccessToken(options);
                    }
                }
            }
            catch (error) {
                const tokenFromStorage = this._getTokenDataFromStorage();
                if (tokenFromStorage) {
                    this.token = this.client.createToken(tokenFromStorage);
                    this.token.expiresIn(new Date(tokenFromStorage.expires));
                    if (this._isTokenExpiring(options)) {
                        /**
                         * Remove token from storage. It will be added back on successful token refresh
                         * The other possibility is that there is an error refreshing the token and this
                         * token should be thrown out until the user is authenticated again
                         */
                        this._removeTokenDataFromStorage();
                        yield this.refreshAccessToken({ expiringRefresh: true });
                    }
                }
                else {
                    console.warn(error, 'Error refreshing access token - redirecting');
                    if (this.clientOptions.loginRedirectUri) {
                        globals_1.window.location.href = `${this.clientOptions.loginRedirectUri}?originalUrl=${encodeURIComponent(globals_1.window.location.href)}`;
                    }
                    else {
                        globals_1.window.location.href = this.client.code.getUri();
                    }
                }
            }
        });
    }
    startAutomaticTokenRefresh(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.token) {
                // Initiate initial auth token exchange
                yield this.refreshAccessToken(options);
            }
            options = options || {};
            if (!this.refreshInterval) {
                this.refreshInterval = globals_1.window.setInterval(() => __awaiter(this, void 0, void 0, function* () {
                    try {
                        if (this._isTokenExpiring(options)) {
                            yield this.refreshAccessToken({ expiringRefresh: true });
                        }
                    }
                    catch (error) {
                        console.warn(error, 'Error in automatic token refresh');
                    }
                }), options.interval || AUTH_TOKEN_INTERVAL);
            }
        });
    }
    stopAutomaticTokenRefresh() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.refreshInterval) {
                globals_1.window.clearInterval(this.refreshInterval);
            }
        });
    }
    getAccessToken() {
        return this.token && this.token.accessToken;
    }
    sign(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.token) {
                throw new Error('Cannot sign request before receiving access token - wait for refreshAccessToken');
            }
            return this.token.sign(options);
        });
    }
    /**
     * Logs out the local session by removing the token from local storage and redirecting to the logout uri
     *
     * @param global indicates a global logout should be performed by making a GET to the globalLogoutUri provided in clientOptions
     */
    logout(global = false) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.stopAutomaticTokenRefresh();
            this._removeTokenDataFromStorage();
            const accessToken = this.getAccessToken();
            if (global && accessToken && this.clientOptions.globalLogoutUri) {
                yield globals_1.window.fetch(this.clientOptions.globalLogoutUri, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                });
            }
            const qs = queryString.stringify({
                client_id: this.clientOptions.clientId,
                logout_uri: this.clientOptions.logoutRedirectUri
            });
            globals_1.window.location.href = `${this.clientOptions.logoutUri}?${qs}`;
        });
    }
}
exports.default = LOAuth;
//# sourceMappingURL=LOAuth.js.map